import { useState } from "react";
import { BellRing, CalendarClock, CalendarPlus, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Btn, Field, Modal } from "@/components/kit";
import { useCompleteReminder, useContactReminder, useDeleteReminder, useSaveReminder } from "@/hooks/queries";

const pad = (n) => String(n).padStart(2, "0");
const toLocalInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const relative = (iso) => {
  const mins = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  const abs = Math.abs(mins);
  const t = abs < 60 ? `${abs} min` : abs < 1440 ? `${Math.round(abs / 60)} hr` : `${Math.round(abs / 1440)} day${Math.round(abs / 1440) === 1 ? "" : "s"}`;
  return mins >= 0 ? `Due in ${t}` : `${t} overdue`;
};
const TONES = {
  overdue: { label: "Overdue", fg: "text-[#DC2626]", iconBg: "bg-[#FEF2F2]", pill: "bg-[#FEF2F2] text-[#B91C1C]" },
  today: { label: "Due today", fg: "text-[#EA580C]", iconBg: "bg-[#FFF7ED]", pill: "bg-[#FFF7ED] text-[#C2410C]" },
  upcoming: { label: "Upcoming", fg: "text-[#0000EC]", iconBg: "bg-[#E5E5FD]", pill: "bg-[#E5E5FD] text-[#0000EC]" },
};
const pretty = (iso) => new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export default function ReminderSection({ contactId }) {
  const current = useContactReminder(contactId).data;
  const save = useSaveReminder(contactId);
  const complete = useCompleteReminder(contactId);
  const del = useDeleteReminder(contactId);
  const [open, setOpen] = useState(false);
  const [when, setWhen] = useState("");
  const [note, setNote] = useState("");
  const tone = TONES[current?.overdue ? "overdue" : current?.dueToday ? "today" : "upcoming"];

  const openEditor = () => {
    const d = current?.dueAt ? new Date(current.dueAt) : new Date(new Date().setHours(9, 0, 0, 0) + 86400000);
    setWhen(toLocalInput(d));
    setNote(current?.note ?? "");
    setOpen(true);
  };
  const run = async (p, ok) => {
    try { await p; toast.success(ok); setOpen(false); } catch { toast.error("Could not update reminder."); }
  };

  return (
    <section className="mt-8" data-testid="reminder-section">
      <h2 className="t-section mb-3">Follow-up Reminder</h2>
      {current && current.status === "pending" ? (
        <div className="rounded-[18px] border border-[#E2E8F0] bg-white overflow-hidden" data-testid="reminder-current">
          <div className="flex items-start gap-3 p-4">
            <span className={`w-12 h-12 rounded-[14px] flex items-center justify-center shrink-0 ${tone.iconBg}`}><BellRing className={`w-6 h-6 ${tone.fg}`} /></span>
            <div className="flex-1 min-w-0">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${tone.pill}`} data-testid="reminder-status">{tone.label}</span>
              <p className="mt-1 text-[16px] font-bold text-[#0F172A]" data-testid="reminder-when">{pretty(current.dueAt)}</p>
              <p className={`text-[13px] font-semibold ${tone.fg}`}>{relative(current.dueAt)}</p>
            </div>
            <button className="w-9 h-9 rounded-full flex items-center justify-center text-[#94A3B8] hover:text-[#DC2626] hover:bg-[#FEF2F2] transition-colors" title="Remove reminder" onClick={() => run(del.mutateAsync(current.id), "Reminder removed.")} data-testid="reminder-delete"><Trash2 className="w-4 h-4" /></button>
          </div>
          {current.note && <p className="mx-4 mb-3 rounded-[12px] bg-[#F8FAFC] px-3 py-2 text-[14px] text-[#475569]" data-testid="reminder-note-text">{current.note}</p>}
          <div className="grid grid-cols-2 border-t border-[#F1F5F9]">
            <button className="h-12 inline-flex items-center justify-center gap-2 text-[14px] font-bold text-[#059669] hover:bg-[#ECFDF5] transition-colors disabled:opacity-60" disabled={complete.isPending} onClick={() => run(complete.mutateAsync(current.id), "Follow-up done ✓ Counts toward your goals.")} data-testid="reminder-complete"><Check className="w-4 h-4" strokeWidth={3} />Mark done</button>
            <button className="h-12 inline-flex items-center justify-center gap-2 text-[14px] font-bold text-[#0000EC] hover:bg-[#F5F5FF] border-l border-[#F1F5F9] transition-colors" onClick={openEditor} data-testid="reminder-edit"><CalendarClock className="w-4 h-4" />Reschedule</button>
          </div>
        </div>
      ) : (
        <button className="t-btn t-btn-outline w-full rounded-full" onClick={openEditor} data-testid="reminder-set"><CalendarPlus className="w-5 h-5" />Set follow-up reminder</button>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Follow-up reminder" description="We'll notify you in your TOTO notifications feed." testid="reminder-modal">
        <div className="flex flex-col gap-4">
          <Field label="Date & time" type="datetime-local" value={when} min={toLocalInput(new Date())} onChange={(e) => setWhen(e.target.value)} testid="reminder-datetime" />
          <Field label="Note" textarea placeholder="e.g. Send pricing proposal" value={note} onChange={(e) => setNote(e.target.value)} testid="reminder-note" />
          <Btn loading={save.isPending} onClick={() => when && run(save.mutateAsync({ dueAt: new Date(when).toISOString(), note: note.trim() || null }), "Follow-up reminder set ✓")} testid="reminder-save">Save reminder</Btn>
        </div>
      </Modal>
    </section>
  );
}
